import React from 'react';
// eslint-disable-next-line import/extensions
import { connect } from 'redux-zero/react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Field, Formik } from 'formik';

import { withLocaleContext } from '../../locales/locale-context.js';
import { validateEmail } from '../../shared/utils.js';
import { actions } from '../../shared/store.js';
import ContactsPageService from './contacts.service.js';
import HomePageService from '../home/home.service.js';
import {
	FormTextBox,
	FormTextArea
} from '../../components/ui-toolkit/form-inputs.js';
import { PrimaryButton } from '../../components/ui-toolkit/buttons.js';
import PageHeader from '../../components/page-header/page-header.component.js';
import ErrorMessage from '../../components/error-message/error-message.component.js';
import NotImplemented from '../../components/not-implemented/not-implemented.js';

import success from '../../assets/icons/success.svg';

import './contacts.scss';

const Success = ({ title, description }) => (
	<div className="center-xs success row">
		<div className="col-xs-12 center-xs">
			<img src={success} alt="Success" />
		</div>
		<div className="col-xs-12 col-md-9 center-xs">
			<h3>{title}</h3>
		</div>
		<div className="col-xs-12 col-md-4 center-xs">
			<h6>{description}</h6>
		</div>
	</div>
);

const HeaderOption = ({ text, link, linkLabel, email, phone }) => (
	<div className="col-xs-12 option-col">
		<div className="option">
			{!email && !phone && (
				<div
					className="description"
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			)}
			{email && (
				<div className="description">
					<a target="_blank" rel="noopener noreferrer" href={`mailto:${text}`}>
						{text}
					</a>
				</div>
			)}
			{phone && (
				<div className="description phone">
					<a target="_blank" rel="noopener noreferrer" href={`tel:${text}`}>
						{text}
					</a>
				</div>
			)}

			{link && linkLabel && (
				<a
					className="googleView"
					target="_blank"
					rel="noopener noreferrer"
					href={link}
				>
					{linkLabel}
				</a>
			)}
		</div>
	</div>
);

class ContactsPage extends React.Component {
	static prefetchData = async language =>
		ContactsPageService.getContactsData(language);

	state = {
		hasError: false,
		hasFormError: false,
		submittedForm: false
	};

	componentDidMount() {
		const { contactsPageData, setLoading } = this.props;
		if (!contactsPageData) {
			this.fetchData();
		} else {
			setLoading(false);
		}
	}

	componentDidUpdate(prevProps) {
		const {
			localeContext: { locale }
		} = this.props;

		if (prevProps.localeContext.locale.value !== locale.value) {
			this.fetchData();
		}
	}

	fetchData() {
		const {
			setLoading,
			setContactsData,
			localeContext: { locale }
		} = this.props;

		setLoading(true);

		ContactsPage.prefetchData(locale.value)
			.then(({ data }) => {
				setContactsData(data);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				this.setState({
					hasError: true
				});
			});
	}

	render() {
		const { hasError, submittedForm, hasFormError } = this.state;
		const { contactsPageData, intl } = this.props;

		if (hasError || !contactsPageData) {
			return <NotImplemented />;
		}
		const {
			address,
			email,
			phoneNumber,
			mapsLink,
			facebookLink,
			linkedinLink,
			twitterLink
		} = contactsPageData;
		return (
			<div className="contacts-page">
				<Helmet>
					<title>
						{intl.formatMessage({
							id: 'navigation.contacts'
						})}
					</title>
				</Helmet>
				<section className="header">
					<div className="container container-fluid ">
						<div className="row">
							<div className="col-xs-12 col-md-7">
								<div className="container">
									<div className="row">
										<div className="col-xs-12" id="contacts-header">
											<PageHeader
												title={<FormattedMessage id="contacts.title" />}
											/>
										</div>
										<div className="col-xs-12">
											<div className="title">
												<h2>
													<FormattedMessage id="contacts.title.first_line" />
												</h2>
											</div>
										</div>
									</div>
									<div className="row options">
										<small className="label">
											<FormattedMessage id="contacts.contact_us" />
										</small>
										<HeaderOption
											text={address}
											link={mapsLink}
											linkLabel={
												<FormattedMessage id="contacts.view_on_google_maps" />
											}
										/>
										<div className="col-xs-12 col-md-6">
											<div className="row">
												<small className="label">
													<FormattedMessage id="contacts.email_us_at" />
												</small>
												<HeaderOption text={email} email />
											</div>
										</div>
										<div className="col-xs-12 col-md-6">
											<div className="row">
												<small className="label">
													<FormattedMessage id="contacts.call_us_at" />
												</small>
												<HeaderOption text={phoneNumber} phone />
											</div>
										</div>
										<small className="label">
											<FormattedMessage id="contacts.follow_us_on" />
												<div className="row social-links">
													{facebookLink && (
														<div className="col-md-4 social-link-item">
															<a
																class="circle"
																href={facebookLink}
																target="_blank"
																rel="noopener noreferrer"
															>
																<svg
																	width="7px"
																	height="15px"
																	viewBox="0 0 7 15"
																	version="1.1"
																	xmlns="http://www.w3.org/2000/svg"
																	className="image"
																>
																	<g
																		id="Symbols"
																		stroke="rgb(81,156,106)"
																		fill="white"
																		strokeWidth="1"
																		fillRule="evenodd"
																	>
																		<g
																			id="Footer"
																			transform="translate(-1215.000000, -91.000000)"
																			fillRule="nonzero"
																		>
																			<g
																				id="Group-2"
																				transform="translate(0.000000, -1.000000)"
																			>
																				<path
																					d="M1216.51301,94.9050224 C1216.51301,95.2830286 1216.51301,96.9702287 1216.51301,96.9702287 L1215,96.9702287 L1215,99.4955709 L1216.51301,99.4955709 L1216.51301,107 L1219.62106,107 L1219.62106,99.4957802 L1221.70671,99.4957802 C1221.70671,99.4957802 1221.90204,98.2848979 1221.99672,96.9609172 C1221.72526,96.9609172 1219.63282,96.9609172 1219.63282,96.9609172 C1219.63282,96.9609172 1219.63282,95.4917533 1219.63282,95.234239 C1219.63282,94.9761667 1219.97169,94.6290245 1220.30662,94.6290245 C1220.64092,94.6290245 1221.34649,94.6290245 1222,94.6290245 C1222,94.2851955 1222,93.097191 1222,92 C1221.12759,92 1220.13507,92 1219.69758,92 C1216.43619,91.9998256 1216.51301,94.5276788 1216.51301,94.9050224 Z"
																					id="Facebook"
																				/>
																			</g>
																		</g>
																	</g>
																</svg>
															</a>
														</div>
													)}
													{twitterLink && (
														<div className="col-md-4 social-link-item">
															<a
																class="circle"
																href={twitterLink}
																target="_blank"
																rel="noopener noreferrer"
															>
																<svg
																	width="17px"
																	height="14px"
																	viewBox="0 0 17 14"
																	version="1.1"
																	xmlns="http://www.w3.org/2000/svg"
																	className="image"
																>
																	<g
																		id="Symbols"
																		stroke="rgb(81,156,106)"
																		fill="white"
																		strokeWidth="1"
																		fillRule="evenodd"
																	>
																		<g
																			id="Footer"
																			transform="translate(-1266.000000, -92.000000)"
																			fillRule="nonzero"
																		>
																			<g
																				id="Group-2"
																				transform="translate(0.000000, -1.000000)"
																			>
																				<path
																					d="M1281.55748,95.019984 C1282.00081,94.6029053 1282.33413,94.0812286 1282.52218,93.4989434 C1282.57586,93.332711 1282.39348,93.1889657 1282.24187,93.2779704 C1281.68216,93.60666 1281.07926,93.8532387 1280.44988,94.0109358 C1280.41336,94.0200866 1280.37568,94.0247235 1280.33788,94.0247235 C1280.22324,94.0247235 1280.11179,93.9825807 1280.02412,93.9060916 C1279.35449,93.3217957 1278.49325,93 1277.5991,93 C1277.21218,93 1276.82154,93.0597058 1276.43798,93.1774759 C1275.24959,93.5423993 1274.33264,94.5129157 1274.04495,95.7103141 C1273.93702,96.1594411 1273.9078,96.6089784 1273.95804,97.0464105 C1273.9638,97.0966782 1273.9398,97.1317631 1273.925,97.1482181 C1273.89902,97.1770656 1273.86217,97.1936027 1273.82391,97.1936027 C1271.20955,96.9537126 1268.86378,95.7129404 1267.20566,93.6992757 C1267.12111,93.5965654 1266.95896,93.6091631 1266.89157,93.7236094 C1266.56687,94.2751185 1266.39527,94.905907 1266.39527,95.5477749 C1266.39527,96.5314635 1266.79446,97.4584829 1267.48784,98.1342867 C1267.19625,98.0658405 1266.91403,97.9580418 1266.65188,97.8138862 C1266.52505,97.7441269 1266.36899,97.8337881 1266.36721,97.9774103 C1266.34906,99.4387452 1267.21324,100.739059 1268.50782,101.324586 C1268.48175,101.325202 1268.45568,101.325489 1268.42957,101.325489 C1268.22436,101.325489 1268.01658,101.305874 1267.81208,101.267178 C1267.66925,101.240177 1267.54926,101.37432 1267.59361,101.511418 C1268.01381,102.810214 1269.14334,103.767558 1270.49504,103.98492 C1269.37321,104.730483 1268.06682,105.123761 1266.70713,105.123761 L1266.28324,105.123515 C1266.15264,105.123515 1266.04218,105.207801 1266.0089,105.333039 C1265.97611,105.45639 1266.0365,105.586512 1266.14787,105.650649 C1267.68003,106.533433 1269.42767,107 1271.20263,107 C1272.7563,107 1274.20963,106.694741 1275.52228,106.092759 C1276.72563,105.540881 1277.78901,104.751985 1278.68287,103.747984 C1279.51556,102.812717 1280.1667,101.733047 1280.61815,100.539014 C1281.04851,99.4008289 1281.27597,98.18636 1281.27597,97.0268779 L1281.27597,96.9716449 C1281.27593,96.7854695 1281.36098,96.6102916 1281.50936,96.4910441 C1282.07243,96.0384292 1282.56276,95.505632 1282.96667,94.9074663 C1283.07331,94.749523 1282.90512,94.5515706 1282.72963,94.6286342 C1282.35191,94.7944562 1281.95982,94.9252754 1281.55748,95.019984 Z"
																					id="Shape"
																				/>
																			</g>
																		</g>
																	</g>
																</svg>
															</a>
														</div>
													)}
													{linkedinLink && (
														<div className="col-md-4 social-link-item">
															<a
																class="circle"
																href={linkedinLink}
																target="_blank"
																rel="noopener noreferrer"
															>
																<svg
																	width="15px"
																	height="15px"
																	viewBox="0 0 15 15"
																	version="1.1"
																	xmlns="http://www.w3.org/2000/svg"
																	className="image"
																>
																	<g
																		id="Symbols"
																		stroke="rgb(81,156,106)"
																		fill="white"
																		strokeWidth="1"
																		fillRule="evenodd"
																	>
																		<g
																			id="Footer"
																			transform="translate(-1237.000000, -91.000000)"
																			fillRule="nonzero"
																		>
																			<g
																				id="Group-2"
																				transform="translate(0.000000, -1.000000)"
																			>
																				<path
																					d="M1252,101.196488 L1252,107 L1248.78501,107 L1248.78501,101.585172 C1248.78501,100.224923 1248.32,99.2966068 1247.15628,99.2966068 C1246.26793,99.2966068 1245.73927,99.9222242 1245.5067,100.527404 C1245.42188,100.743753 1245.40005,101.044883 1245.40005,101.347728 L1245.40005,107 L1242.18398,107 C1242.18398,107 1242.2273,97.8290592 1242.18398,96.8788814 L1245.39974,96.8788814 L1245.39974,98.3135094 C1245.39322,98.3242393 1245.38474,98.3358086 1245.3786,98.34621 L1245.39974,98.34621 L1245.39974,98.3135094 C1245.82698,97.6248631 1246.59,96.6411088 1248.29786,96.6411088 C1250.41378,96.6410723 1252,98.0876711 1252,101.196488 Z M1238.81984,92 C1237.71963,92 1237,92.7552153 1237,93.7483492 C1237,94.7198408 1237.69881,95.4979757 1238.77716,95.4979757 L1238.79864,95.4979757 C1239.92016,95.4979757 1240.61764,94.7199868 1240.61764,93.7483492 C1240.59651,92.7552153 1239.92016,92 1238.81984,92 Z M1237.19101,107 L1240.40585,107 L1240.40585,96.8788814 L1237.19101,96.8788814 L1237.19101,107 Z"
																					id="LinkedIn"
																				/>
																			</g>
																		</g>
																	</g>
																</svg>
															</a>
														</div>
													)}
												</div>
										</small>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="polygon polygon-1" />
					<div className="polygon polygon-2" />
					<div className="polygon polygon-3" />
					<div className="polygon polygon-4" />
					<div className="polygon polygon-5" />
					<div className="polygon polygon-6" />
					<div className="polygon polygon-7" />
					<div className="polygon polygon-8" />
				</section>
				<section className="question">
					<div className="container container-fluid">
						{!submittedForm ? (
							<div className="title center-xs row">
								<div className="col-xs-12 center-xs">
									<h2>
										<FormattedMessage id="homepage.question.title" />{' '}
									</h2>
								</div>
								<div className="col-xs-10 col-md-6">
									<h6>
										<FormattedMessage id="homepage.question.description1" />
										<FormattedMessage id="homepage.question.description2" />
									</h6>
								</div>
								<div className="col-xs-12 col-md-8 center-xs">
									<div className="container">
										<Formik
											enableReinitialize
											initialValues={{
												email: '',
												question: ''
											}}
											onSubmit={(values, { setSubmitting, resetForm }) => {
												setSubmitting(true);
												this.setState({ hasFormError: false });

												HomePageService.submitQuestion(values)
													.then(() => {
														setSubmitting(false);
														this.setState({
															submittedForm: true,
															hasFormError: false
														});
													})
													.catch(() => {
														setSubmitting(false);
														this.setState({
															hasFormError: true
														});
													});
											}}
										>
											{({
												errors,
												dirty,
												touched,
												handleSubmit,
												isSubmitting,
												isValid
											}) => (
												<form onSubmit={handleSubmit} noValidate>
													<div className="row center-xs">
														<div className="col-xs-12 col-md-7 center-xs">
															<Field
																component={FormTextBox}
																type="email"
																name="email"
																placeholder={intl.formatMessage({
																	id: 'homepage.form.email_label'
																})}
																fullWidth
																error={errors.email && touched.email}
																validate={value => {
																	let error = false;
																	if (!validateEmail(value)) {
																		error = intl.formatMessage({
																			id: 'errors.invalid_email'
																		});
																	}
																	if (!value) {
																		error = intl.formatMessage({
																			id: 'errors.required'
																		});
																	}
																	return error;
																}}
																validationMessage={errors.email}
																required
															/>
														</div>
														<div className="col-xs-12 col-md-7 center-xs">
															<Field
																component={FormTextArea}
																name="question"
																placeholder={intl.formatMessage({
																	id: 'homepage.form.question_label'
																})}
																rows={7}
																fullWidth
																error={errors.question && touched.question}
																validate={value => {
																	let error = false;
																	if (value.length < 10) {
																		error = intl.formatMessage({
																			id: 'errors.too_short'
																		});
																	}
																	if (!value) {
																		error = intl.formatMessage({
																			id: 'errors.required'
																		});
																	}

																	return error;
																}}
																validationMessage={errors.question}
																required
															/>
														</div>
														<div className="col-xs-12 col-md-7 center-xs">
															<PrimaryButton
																type="submit"
																disabled={!isValid || !dirty || isSubmitting}
																extraClasses="full-width"
															>
																<FormattedMessage id="homepage.form.send_message" />
															</PrimaryButton>
														</div>
														{hasFormError && (
															<div className="col-xs-12 col-md-7 center-xs">
																<ErrorMessage
																	label1={
																		<FormattedMessage id="error.form.submitting" />
																	}
																	label2={
																		<FormattedMessage id="error.form.submitting.try_again" />
																	}
																/>
															</div>
														)}
													</div>
												</form>
											)}
										</Formik>
									</div>
								</div>
							</div>
						) : (
							<Success
								title={intl.formatMessage({
									id: 'homepage.form.success.title'
								})}
								description={intl.formatMessage({
									id: 'homepage.form.success.description'
								})}
							/>
						)}
					</div>
				</section>
			</div>
		);
	}
}

const mapToProps = ({ contactsPageData }) => ({
	contactsPageData
});

export default withLocaleContext(
	injectIntl(connect(mapToProps, actions)(ContactsPage))
);

export { ContactsPage };
